import { useRef } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import { QRCodeSVG } from "qrcode.react"
import styled from "styled-components"

import { ReactComponent as IconExport } from "assets/svg/export-icon.svg"

import { useUserProfile } from "hooks/useUserProfile"

import { Card, CardHeader, CardTitle } from "./shared"

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f1f5f9;
`

const LinkText = styled.code`
  flex: 1;
  font-family: monospace;
  font-size: 14px;
  color: var(--text-primary);
  overflow-wrap: anywhere;
`

const CopyButton = styled.button`
  border: none;
  cursor: pointer;
  height: 36px;
  padding: 8px 16px;
  border-radius: 20px;
  background: #43e0f8;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.084px;
  color: #134f66;

  &:hover {
    opacity: 0.9;
  }
`

const QrRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`

const QrBox = styled.div`
  flex-shrink: 0;
  align-self: flex-start;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid var(--border-default);
  background: #fff;
  line-height: 0;
`

const QrHint = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 21px;
  color: #64748b;
`

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  margin-right: 8px;
  margin-bottom: 8px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.084px;
  color: #0f172a;

  &:hover {
    background: var(--surface-hover);
  }

  svg {
    width: 20px;
    height: 20px;

    /* the shared export icon is cyan; the design's tray icon is dark like the label */
    path {
      stroke: #0f172a;
    }
  }
`

const PreviewLink = styled(Link)`
  flex-shrink: 0;
  white-space: nowrap;
  color: #0f172a;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.084px;

  &:hover {
    text-decoration: underline;
  }
`

const QR_SIZE = 512

const triggerDownload = (href: string, fileName: string) => {
  const anchor = document.createElement("a")
  anchor.href = href
  anchor.download = fileName
  anchor.click()
}

export const ShareLinkCard = () => {
  const qrWrapperRef = useRef<HTMLDivElement>(null)
  const { userProfile } = useUserProfile()

  const referralSlug = userProfile?.referral_agent?.slug
  const brokerageName = userProfile?.referral_agent?.brokerage_name
  const referralLinkUrl = `${window.location.origin}/r/${referralSlug}`
  const referralLinkLabel = referralLinkUrl.replace(/^https?:\/\//, "")

  const onCopy = async () => {
    await navigator.clipboard.writeText(referralLinkUrl)
    toast.success("Link copied to clipboard!")
  }

  const getQrSvg = () => qrWrapperRef.current?.querySelector("svg")

  const serializeQrSvg = (svg: SVGSVGElement) => new XMLSerializer().serializeToString(svg)

  const onDownloadSvg = () => {
    const svg = getQrSvg()
    if (!svg) return

    const blob = new Blob([serializeQrSvg(svg)], { type: "image/svg+xml" })
    const url = URL.createObjectURL(blob)
    triggerDownload(url, "referral-qr.svg")
    // revoking synchronously can race the download start in some browsers
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const onDownloadPng = () => {
    const svg = getQrSvg()
    if (!svg) return

    const blob = new Blob([serializeQrSvg(svg)], { type: "image/svg+xml" })
    const url = URL.createObjectURL(blob)
    const image = new Image()

    image.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = QR_SIZE
      canvas.height = QR_SIZE
      const context = canvas.getContext("2d")
      if (context) {
        context.fillStyle = "#fff"
        context.fillRect(0, 0, QR_SIZE, QR_SIZE)
        context.drawImage(image, 0, 0, QR_SIZE, QR_SIZE)
        triggerDownload(canvas.toDataURL("image/png"), "referral-qr.png")
      }
      URL.revokeObjectURL(url)
    }

    image.src = url
  }

  if (!referralSlug) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Share your personalized referral link</CardTitle>
        <PreviewLink to={`/r/${referralSlug}`} target="_blank">
          Preview landing
        </PreviewLink>
      </CardHeader>
      <LinkRow>
        <LinkText>{referralLinkLabel}</LinkText>
        <CopyButton type="button" onClick={onCopy}>
          Copy
        </CopyButton>
      </LinkRow>
      <QrRow>
        <QrBox ref={qrWrapperRef}>
          <QRCodeSVG value={referralLinkUrl} size={120} />
        </QrBox>
        <div>
          <QrHint>
            {`Drop this in listings, closing packets, and follow-ups. When your client scans, they land on a page that shows your name${brokerageName ? ` and ${brokerageName} branding` : ""}.`}
          </QrHint>
          <DownloadButton type="button" onClick={onDownloadPng}>
            <IconExport />
            QR (PNG)
          </DownloadButton>
          <DownloadButton type="button" onClick={onDownloadSvg}>
            <IconExport />
            QR (SVG)
          </DownloadButton>
        </div>
      </QrRow>
    </Card>
  )
}
